import { parseToRgb } from 'polished';
// tslint:disable-next-line
import vdsTheme from '@visa/vds-web/dist/_json/theme.js';

import homeImage from '../static/images/home-image.svg';
import logo from '../static/images/visa-logo.svg';
import {
  adjustColor,
  isColor,
  isPrimaryColor,
  keyMatchesString,
  normalizeColor
} from './helpers';
import { keysToIgnore, valuesToIgnore } from './ignoreList';
import { IThemeOptions } from './index';
import { variables, vaultColors, wcag21Colors } from './variables';

// Manipulate vds variable values before spreading it into the theme
for (const vdsKey in vdsTheme) {
  if (vdsTheme.hasOwnProperty(vdsKey)) {
    const vdsValue = vdsTheme[vdsKey];
    const isHexColor = isColor(vdsValue) && vdsValue.startsWith('#');
    let newVdsValue = vdsValue;

    if (isHexColor) {
      /* 
        Manipulation step 1: Hex color character expansion
        - Determine if this is a hex value that has been shortened to 3 hex characters instead of 6
        - i.e. #222 vs #222222 or #234 vs #223344
        - If it is, force it to be the longer version so that they are all consistently 6 chars long
        - This makes the values much easier to compare against and consolidate together
      */

      // Remove the # and get an array of hex chars
      const hexChars = vdsValue.slice(1);
      const isShortenedHex = hexChars.length === 3;

      if (isShortenedHex) {
        newVdsValue = `#${hexChars[0]}${hexChars[0]}${hexChars[1]}${
          hexChars[1]
        }${hexChars[2]}${hexChars[2]}`;
      }

      /* 
        Manipulation step 2: Interim color remapping solution:
        - It is much quicker and easier to force remap these colors in the vds theme
        - However, eventually, it would be ideal to do this remapping in each cybs-ui component's styles file
        - Then we would be able to discard the vds theme and all of its variables entirely
      */
      const colorMap = {
        [vaultColors.blue]: wcag21Colors.trueBlue,
        [vaultColors.cloudy]: wcag21Colors.stoneGrey,
        [vaultColors.indigo]: wcag21Colors.midnightBlue,
        [vaultColors.fog]: wcag21Colors.pearlGrey,
        [vaultColors.lightBlue]: wcag21Colors.cobaltBlue,
        [vaultColors.night]: wcag21Colors.midnightBlue,
        [vaultColors.plum]: wcag21Colors.skyBlue,
        [vaultColors.smoke]: wcag21Colors.silverGrey
      };

      // Use .some here to avoid unecessary looping (stops executing once a call returns true)
      Object.keys(colorMap).some(colorMapKey => {
        if (vdsValue === colorMapKey) {
          newVdsValue = colorMap[colorMapKey];
          return true;
        }
        return false;
      });
    }

    /* 
      Manipulation step 3: Links:
      - For now, force all these link variables to be the new cybs blue
      - Also force disabbled link variable to the new cybs gray
    */
    if (vdsKey.startsWith('linkColor')) {
      newVdsValue =
        vdsKey === 'linkColorDisabled'
          ? variables.colors.action.link.disabled
          : variables.colors.action.link.default;
    }

    // Finally, set the current vds variable value to the one that's been manipulated
    vdsTheme[vdsKey] = newVdsValue;
  }
}

const baseTheme = {
  ...vdsTheme,

  // Action
  actionPrimaryLight: variables.colors.action.primary.light,
  actionSecondaryLight: variables.colors.action.secondary.light,
  linkUnderline: 'underline',
  linkUnderlineHover: 'none',
  primary: variables.colors.action.primary.default,
  primaryDark: variables.colors.action.primary.dark,
  primaryExtraDark1: variables.colors.action.primary.extraDark1,
  primaryExtraDark2: variables.colors.action.primary.extraDark2,
  primaryExtraLight: variables.colors.action.primary.extraLight,
  primaryLight: variables.colors.action.primary.light,
  secondary: variables.colors.action.secondary.default,

  // Application
  applicationBackground: variables.colors.application.background,
  applicationDisabled: variables.colors.application.disabled,
  applicationOverlay: variables.colors.application.overlay,
  applicationTopbar: variables.colors.application.topbar,

  // Brand
  brandCompany: 'CyberSource',
  brandLogoUrl: logo,
  homeImageUrl: homeImage,

  // Components
  avatarLogoBackground: variables.colors.action.secondary.default,

  footerBackground: variables.colors.component.footer.background,
  footerLink: variables.colors.component.footer.link,
  footerText: variables.colors.component.footer.text,

  navbarBackground: variables.colors.component.navbar.background,
  navbarBackgroundSelected:
    variables.colors.component.navbar.backgroundSelected,
  navbarDropdownSelected: variables.colors.component.navbar.dropdownSelected,
  navbarForeground: variables.colors.component.navbar.foreground,
  navbarIndicatorBar: variables.colors.component.navbar.indicatorBar,
  navbarRollover: variables.colors.component.navbar.rollover,

  tableAlt: variables.colors.component.table.alt,
  tableHighlight: variables.colors.component.table.highlight,
  tableSelected: variables.colors.component.table.selected,

  topbarBackground: variables.colors.component.topbar.background,
  topbarForeground: variables.colors.component.topbar.foreground,
  topbarPartnerLink: variables.colors.component.topbar.partnerLink,
  topbarPortalText: variables.colors.component.topbar.portalText,

  badgeCriticalBackgroundColor: variables.colors.status.error.background,
  badgeCriticalBorder: variables.colors.status.error.foreground,
  badgeCriticalFill: variables.colors.status.error.foreground,
  badgeNeutralBackgroundColor: variables.colors.status.information.background,
  badgeNeutralBorder: variables.colors.status.information.foreground,
  badgeNeutralFill: variables.colors.status.information.foreground,
  badgeStableBackgroundColor: variables.colors.status.success.background,
  badgeStableBorder: variables.colors.status.success.foreground,
  badgeStableFill: variables.colors.status.success.foreground,
  badgeWarningBackgroundColor: variables.colors.status.warning.background,
  badgeWarningBorder: variables.colors.status.warning.foreground,
  badgeWarningFill: variables.colors.status.warning.foreground,

  notificationError: variables.colors.status.error.foreground,
  notificationErrorLight: variables.colors.status.error.background,
  notificationInformation: variables.colors.status.information.foreground,
  notificationInformationLight: variables.colors.status.information.background,
  notificationSuccess: variables.colors.status.success.foreground,
  notificationSuccessLight: variables.colors.status.success.background,
  notificationWarning: variables.colors.status.warning.foreground,
  notificationWarningLight: variables.colors.status.warning.background,

  // Element
  elementDark: variables.colors.element.dark,
  elementDefault: variables.colors.element.default,
  elementHighlight: variables.colors.element.highlight,
  elementLight: variables.colors.element.light,

  // Misc
  miscDark: variables.colors.misc.dark,
  miscExtraDark: variables.colors.misc.extraDark,
  miscExtraLight: variables.colors.misc.extraLight,
  miscLight: variables.colors.misc.light,
  miscMedium: variables.colors.misc.medium,
  miscTransparent: variables.colors.misc.transparent,
  miscWhite: variables.colors.misc.white
};

// Automatically assigns color keys into categories
const generateKeysAuto = () => {
  const primaryAuto: string[] = [];
  const secondaryAuto: string[] = [];
  const tertiaryAuto: string[] = [];
  const linkAuto: string[] = [];

  Object.keys(vdsTheme).forEach((key: string) => {
    const value: any = vdsTheme[key];
    const isKeyIgnored = keysToIgnore.indexOf(key) > -1;
    const isValueIgnored = valuesToIgnore.indexOf(value) > -1;
    const isKeyViable = isColor(value) && !isKeyIgnored && !isValueIgnored;

    if (isKeyViable) {
      try {
        const rgbColor = parseToRgb(value);

        if (keyMatchesString(key, 'tertiary')) {
          tertiaryAuto.push(key);
        } else if (keyMatchesString(key, 'link')) {
          linkAuto.push(key);
        } else if (isPrimaryColor(key, rgbColor)) {
          primaryAuto.push(key);
        } else {
          secondaryAuto.push(key);
        }
      } catch (e) {
        console.error(key, value, e);
      }
    }
  });

  return {
    linkKeysAuto: linkAuto,
    primaryKeysAuto: primaryAuto,
    secondaryKeysAuto: secondaryAuto,
    tertiaryKeysAuto: tertiaryAuto
  };
};

const {
  primaryKeysAuto,
  secondaryKeysAuto,
  tertiaryKeysAuto,
  linkKeysAuto
} = generateKeysAuto();

const primaryKeys: string[] = [
  'actionPrimaryLight',
  'primaryDark',
  'applicationBackground',
  'applicationDisabled',
  'tableHighlight',
  'tableSelected',
  'primaryExtraDark1',
  'primaryExtraDark2',
  ...primaryKeysAuto
];
const secondaryKeys: string[] = [
  'actionSecondaryLight',
  'avatarLogoBackground',
  'elementDark',
  'elementDefault',
  'elementHighlight',
  'elementLight',
  ...secondaryKeysAuto
];
const tertiaryKeys: string[] = tertiaryKeysAuto;
const linkKeys: string[] = linkKeysAuto;

const PRIMARY_KEY = 'actionPrimary';
const SECONDARY_KEY = 'secondary';
const ACCENT_KEY = 'progressBackground';

// Generates new color variables for specific categories
const getNewVariables = (themeOptions: IThemeOptions) => {
  const newPrimaryVariables = {};
  const newSecondaryVariables = {};
  const newTertiaryVariables = {};
  const newLinkVariables = {};
  const newFooterVariables = {};
  const newNavbarVariables = {};
  const newTopbarVariables = {};

  if (themeOptions) {
    const a11y = themeOptions.accessible || false;
    const customCore = themeOptions.core || themeOptions.isSwatch;
    const customComponent = themeOptions.component;

    if (customCore) {
      const {
        primary: customPrimary,
        secondary: customSecondary,
        tertiary: customTertiary
      } = customCore;

      // TODO: remove legacy support in a future major release
      // Support legacy name for link color
      const customLink = customCore.link || customCore.linkColor;

      // Assign new primary colors
      // Changing primary will also affect secondary, tertiary, and link colors (if they aren't provided)
      if (customPrimary) {
        const controlPrimary: string = baseTheme.primary;
        const variablePrimary: string = a11y
          ? normalizeColor(controlPrimary, customPrimary)
          : customPrimary;

        primaryKeys.forEach(primaryKey => {
          if (!a11y && baseTheme[primaryKey] === baseTheme.primary) {
            newPrimaryVariables[primaryKey] = variablePrimary;
          } else {
            newPrimaryVariables[primaryKey] = adjustColor({
              a11y,
              control: controlPrimary,
              experimental: baseTheme[primaryKey],
              variable: variablePrimary
            });
          }
        });
      }

      // Assign new secondary colors
      if (customSecondary || customPrimary) {
        // tslint:disable-next-line: prettier
        const customColor = customSecondary || (customPrimary as string);
        const controlSecondary: string = baseTheme.secondary;
        const variableSecondary: string = a11y
          ? adjustColor({
              a11y,
              control: baseTheme.primary,
              experimental: baseTheme.secondary,
              variable: customColor
            })
          : customColor;

        secondaryKeys.forEach(secondaryKey => {
          if (
            customSecondary &&
            baseTheme[secondaryKey] === baseTheme.secondary
          ) {
            newSecondaryVariables[secondaryKey] = variableSecondary;
          } else {
            newSecondaryVariables[secondaryKey] = adjustColor({
              a11y,
              control: controlSecondary,
              experimental: baseTheme[secondaryKey],
              variable: variableSecondary
            });
          }
        });
      }

      // Assign new tertiary colors
      if (customTertiary || customPrimary) {
        const customColor = customTertiary || (customPrimary as string);
        // Avoid using baseTheme.tertiary as a control because it is gray (throws off the color adjustment)
        const controlTertiary: string = baseTheme.linkColor;
        const variableTertiary: string = a11y
          ? normalizeColor(controlTertiary, customColor)
          : customColor;

        tertiaryKeys.forEach(tertiaryKey => {
          if (baseTheme[tertiaryKey] !== baseTheme.tertiary) {
            newTertiaryVariables[tertiaryKey] = adjustColor({
              a11y,
              control: controlTertiary,
              experimental: baseTheme[tertiaryKey],
              variable: customColor
            });
          } else {
            newTertiaryVariables[tertiaryKey] = variableTertiary;
          }
        });
      }

      // Assign new link colors
      if (customLink) {
        const controlLink: string = baseTheme.linkColor;
        const variableLink: string = a11y
          ? normalizeColor(controlLink, customLink)
          : customLink;

        linkKeys.forEach(linkKey => {
          if (baseTheme[linkKey] !== baseTheme.linkColor) {
            newLinkVariables[linkKey] = adjustColor({
              a11y,
              control: controlLink,
              experimental: baseTheme[linkKey],
              variable: variableLink
            });
          } else {
            newLinkVariables[linkKey] = variableLink;
          }
        });
      }
    }

    if (customComponent) {
      const {
        footer: customFooter,
        navbar: customNavbar,
        topbar: customTopbar
      } = customComponent;

      if (customFooter) {
        const { background, link, text } = customFooter;

        if (background) {
          Object.assign(newFooterVariables, { footerBackground: background });
        }
        if (link) {
          Object.assign(newFooterVariables, { footerLink: link });
        }
        if (text) {
          Object.assign(newFooterVariables, { footerText: text });
        }
      }

      if (customNavbar) {
        const {
          background,
          foreground,
          backgroundSelected,
          indicatorBar,
          dropdownSelected,
          rollover
        } = customNavbar;

        if (background) {
          Object.assign(newNavbarVariables, { navbarBackground: background });
        }
        if (foreground) {
          Object.assign(newNavbarVariables, { navbarForeground: foreground });
        }
        if (backgroundSelected) {
          Object.assign(newNavbarVariables, {
            navbarBackgroundSelected: backgroundSelected
          });
        }
        if (indicatorBar) {
          Object.assign(newNavbarVariables, {
            navbarIndicatorBar: indicatorBar
          });
        }
        if (dropdownSelected) {
          Object.assign(newNavbarVariables, {
            navbarDropdownSelected: dropdownSelected
          });
        }
        if (rollover) {
          Object.assign(newNavbarVariables, {
            navbarRollover: rollover
          });
        }
      }

      if (customTopbar) {
        const {
          background,
          foreground,
          partnerLink,
          portalText
        } = customTopbar;

        if (background) {
          Object.assign(newTopbarVariables, { topbarBackground: background });
        }
        if (foreground) {
          Object.assign(newTopbarVariables, { topbarForeground: foreground });
        }
        if (partnerLink) {
          Object.assign(newTopbarVariables, { topbarPartnerLink: partnerLink });
        }
        if (portalText) {
          Object.assign(newTopbarVariables, { topbarPortalText: portalText });
        }
      }
    }
  }

  return {
    ...baseTheme,
    ...newPrimaryVariables,
    ...newSecondaryVariables,
    ...newTertiaryVariables,
    ...newLinkVariables,
    ...newFooterVariables,
    ...newNavbarVariables,
    ...newTopbarVariables
  };
};

// Colors
const colors = (themeOptions: IThemeOptions) => {
  const a11y = false;
  const theme = {
    // Accent
    accent: {
      default: getNewVariables(themeOptions)[ACCENT_KEY],
      disabled: adjustColor({
        a11y,
        control: baseTheme.actionPrimary,
        experimental: baseTheme.actionPrimaryDisabled,
        variable: getNewVariables(themeOptions)[ACCENT_KEY]
      }),
      light: adjustColor({
        a11y,
        control: baseTheme.actionPrimary,
        experimental: baseTheme.actionPrimaryLight,
        variable: getNewVariables(themeOptions)[ACCENT_KEY]
      })
    },

    // Action
    action: {
      primary: {
        default: getNewVariables(themeOptions)[PRIMARY_KEY],
        disabled: getNewVariables(themeOptions).actionPrimaryDisabled,
        light: getNewVariables(themeOptions).actionPrimaryLight
      },
      secondary: {
        default: getNewVariables(themeOptions)[SECONDARY_KEY],
        disabled: getNewVariables(themeOptions).actionSecondaryDisabled,
        light: getNewVariables(themeOptions).actionSecondaryLight
      }
    },

    // Application
    application: {
      background: getNewVariables(themeOptions).applicationBackground,
      disabled: getNewVariables(themeOptions).applicationDisabled,
      overlay: getNewVariables(themeOptions).applicationOverlay,
      topbar: getNewVariables(themeOptions).applicationTopbar
    },

    // Elements
    element: {
      dark: getNewVariables(themeOptions).elementDark,
      default: getNewVariables(themeOptions).elementDefault,
      highlight: getNewVariables(themeOptions).elementHighlight,
      light: getNewVariables(themeOptions).elementLight
    },

    // Shades
    misc: {
      dark: getNewVariables(themeOptions).miscDark,
      extraDark: getNewVariables(themeOptions).miscExtraDark,
      extraLight: getNewVariables(themeOptions).miscExtraLight,
      light: getNewVariables(themeOptions).miscLight,
      medium: getNewVariables(themeOptions).miscMedium,
      transparent: getNewVariables(themeOptions).miscTransparent,
      white: getNewVariables(themeOptions).miscWhite
    },

    // Primary colors
    primaries: {
      dark: getNewVariables(themeOptions).primaryDark,
      default: getNewVariables(themeOptions).primary,
      extraDark1: getNewVariables(themeOptions).primaryExtraDark1,
      extraDark2: getNewVariables(themeOptions).primaryExtraDark2,
      extraLight: getNewVariables(themeOptions).primaryExtraLight,
      light: getNewVariables(themeOptions).primaryLight
    },

    // Status
    status: {
      negative: {
        default: getNewVariables(themeOptions).notificationError,
        light: getNewVariables(themeOptions).notificationErrorLight
      },
      neutral: {
        default: getNewVariables(themeOptions).notificationInformation,
        light: getNewVariables(themeOptions).notificationInformationLight
      },
      positive: {
        default: getNewVariables(themeOptions).notificationSuccess,
        light: getNewVariables(themeOptions).notificationSuccessLight
      },
      warning: {
        default: getNewVariables(themeOptions).notificationWarning,
        light: getNewVariables(themeOptions).notificationWarningLight
      }
    },

    // Tables & Lists
    table: {
      alt: getNewVariables(themeOptions).tableAlt,
      highlight: getNewVariables(themeOptions).tableHighlight,
      selected: getNewVariables(themeOptions).tableSelected
    },

    type: {
      body: getNewVariables(themeOptions).fontColor
    }
  };

  return theme;
};

// Components
const application = (themeOptions: IThemeOptions) => ({
  home: {
    image: getNewVariables(themeOptions).homeImageUrl || baseTheme.homeImageUrl
  },
  menu: {
    items: {
      iconSize: 48
    }
  },
  panel: {
    header: {
      height: '6.5rem'
    },
    height: '100vh'
  },
  topbar: {
    height: 56
  }
});

// Brand
const brand = (themeOptions: IThemeOptions) => ({
  logo: getNewVariables(themeOptions).brandLogoUrl || baseTheme.brandLogoUrl,
  name: getNewVariables(themeOptions).brandCompany || baseTheme.brandCompany
});

export interface IThemeVariables {
  CSS_VARS: any;
  brand: object;
  colors: any;
  layout: {
    application: object;
    spacing: object;
  };
  misc: any;
  type: any;
}

export const getThemeVariables = (
  themeOptions: IThemeOptions
): IThemeVariables => ({
  CSS_VARS: getNewVariables(themeOptions),
  brand: brand(themeOptions),
  colors: colors(themeOptions),
  layout: {
    application: application(themeOptions),
    spacing: variables.layout.spacing
  },
  misc: variables.misc,
  type: variables.type
});
